/**
 * 🎁 폰 「내 전달 목록」 — /m/gifts (v2.303.0 · 2026-10-26 · 명절선물 2차 묶음 B3)
 *
 * 명절 무렵 밖에서 선물을 들고 다니며 「누구에게 줬나」를 폰으로 체크하는 화면. D-45·D-14 포털챗봇 알림의 링크가 여기로 온다.
 *   · 처음엔 **내 담당 묶음**만(서버 owners.ts — 「대표」 묶음은 경영지원부) · [전체] · 담당자별로 바꿀 수 있다
 *   · 「안 끝난 것만」이 기본 · 직접전달을 위로(들고 가서 주는 것) → 택배 → 백화점 직배송 → 미정
 *   · 줄마다 📞 전화 걸기 · 🗺 지도 · 큰 [완료] 단추(누르면 바로 저장 · 다시 누르면 되돌림)
 * ⭐이 앱은 화면을 0.85배로 그린다 — 손가락 단추는 44px 이상으로.
 * 쓰는 사람 = sales 이상(서버 가드 · 조회전용은 403 문구를 보여 준다).
 */

import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MobileAppTabs } from '../components/MobileAppTabs';
import * as giftApi from '../lib/holiday-gift-api';
import { STATUS_META, type GiftLine, type GiftMethod } from '../lib/holiday-gift-api';
import { LoadingLines } from '../components/ui/Skeleton';

const METHOD_RANK: Record<GiftMethod, number> = { 직접전달: 0, 택배: 1, 백화점직배송: 2 };
const METHOD_CHIP: Record<GiftMethod, string> = {
  직접전달: 'bg-amber-100 text-amber-800 border-amber-300',
  택배: 'bg-sky-50 text-sky-800 border-sky-200',
  백화점직배송: 'bg-violet-50 text-violet-800 border-violet-200',
};

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const t = new Date(`${iso}T00:00:00+09:00`).getTime();
  const now = new Date(Date.now() + 9 * 3600 * 1000);
  const today = new Date(`${now.toISOString().slice(0, 10)}T00:00:00+09:00`).getTime();
  return Math.round((t - today) / 86400000);
}

function whereOf(l: GiftLine): string {
  return l.partnerName ?? (l.partnerNone ? (l.dept || l.recipient) : l.recipient);
}

export function MobileGiftList() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const eventParam = Number(params.get('event')) || null;
  const q = useQuery({ queryKey: ['holiday-gift-mine', eventParam], queryFn: () => giftApi.getMine(eventParam), staleTime: 0 });
  /** 'mine' = 내 담당 묶음 · 'all' = 전체 · 그 밖 = 담당자 글자 하나 */
  const [who, setWho] = useState<string>('mine');
  const [onlyOpen, setOnlyOpen] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const d = q.data;
  const myOwners = d?.myOwners ?? [];
  const effWho = who === 'mine' && myOwners.length === 0 ? 'all' : who;

  const doneM = useMutation({
    mutationFn: (p: { id: number; isDone: boolean }) => giftApi.patchLine(p.id, { isDone: p.isDone }),
    onSuccess: () => { setErr(null); void qc.invalidateQueries({ queryKey: ['holiday-gift-mine'] }); },
    onError: (e) => setErr((e as Error).message),
  });

  const inScope = useMemo(() => (d?.lines ?? []).filter((l) => (
    effWho === 'all' ? true : effWho === 'mine' ? myOwners.includes(l.owner) : l.owner === effWho
  )), [d, effWho, myOwners]);
  const shown = useMemo(() => inScope
    .filter((l) => !onlyOpen || !l.isDone)
    .sort((a, b) => (METHOD_RANK[a.method as GiftMethod] ?? 3) - (METHOD_RANK[b.method as GiftMethod] ?? 3)
      || whereOf(a).localeCompare(whereOf(b), 'ko') || a.id - b.id), [inScope, onlyOpen]);
  const doneCount = inScope.filter((l) => l.isDone).length;

  const header = (
    <header className="sticky top-0 z-20 bg-slate-900 text-white px-3 py-2 shadow">
      <div className="flex items-center justify-between gap-2">
        <h1 className="sr-only">명절선물 내 전달 목록</h1>
        <MobileAppTabs />
        <Link to="/m/inventory" className="px-2 py-1 bg-slate-700 active:bg-slate-600 rounded text-[11px] whitespace-nowrap">재고 목록</Link>
      </div>
    </header>
  );

  if (q.isPending) return <div className="min-h-full">{header}<div className="py-16 text-center text-slate-400"><LoadingLines /></div></div>;
  if (q.isError || !d) {
    const msg = (q.error as Error | null)?.message ?? '';
    return (
      <div className="min-h-full">{header}
        <div className="p-4 text-center text-sm text-rose-600">불러오기 실패: {msg}</div>
      </div>
    );
  }
  if (!d.event) return <div className="min-h-full">{header}<div className="p-6 text-center text-slate-500">아직 적어 둔 명절이 없습니다.</div></div>;

  const ev = d.event;
  const dd = daysUntil(ev.holidayDate);
  const chips: { key: string; label: string; n: number }[] = [
    ...(myOwners.length > 0 ? [{ key: 'mine', label: `내 담당(${myOwners.join('·')})`, n: (d.lines ?? []).filter((l) => myOwners.includes(l.owner)).length }] : []),
    { key: 'all', label: '전체', n: d.lines.length },
    ...d.owners.filter((o) => !(myOwners.length === 1 && myOwners[0] === o)).map((o) => ({ key: o, label: o, n: d.lines.filter((l) => l.owner === o).length })),
  ];

  return (
    <div className="h-full overflow-y-auto bg-slate-50" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {header}
      <div className="bg-white border-b border-slate-200 px-3 py-2 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="font-bold text-slate-900 truncate">{ev.kind === '설' ? '🧧' : '🌕'} {ev.title}</div>
            <div className="text-xs text-slate-500">
              {STATUS_META[ev.status].icon} {ev.status}
              {ev.holidayDate && <> · 명절 당일 {ev.holidayDate}{dd != null && (dd > 0 ? ` · D-${dd}` : dd === 0 ? ' · 오늘' : ` · ${-dd}일 지남`)}</>}
            </div>
          </div>
          {d.events.length > 1 && (
            <select className="ui-input ui-input-sm w-32 shrink-0" value={ev.id} aria-label="명절 고르기"
              onChange={(e) => { const next = new URLSearchParams(params); next.set('event', e.target.value); setParams(next, { replace: true }); setWho('mine'); }}>
              {d.events.map((x) => <option key={x.id} value={x.id}>{x.title}</option>)}
            </select>
          )}
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-0.5" role="group" aria-label="담당 고르기">
          {chips.map((c) => (
            <button key={c.key} type="button" onClick={() => setWho(c.key)} aria-pressed={effWho === c.key}
              className={`shrink-0 min-h-[36px] px-3 rounded-full border text-sm whitespace-nowrap ${effWho === c.key ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-700 border-slate-300 active:bg-slate-100'}`}>
              {c.label} <span className="opacity-70">{c.n}</span>
            </button>
          ))}
        </div>
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-600">완료 <b className="text-slate-900">{doneCount}</b> / {inScope.length}</span>
          <label className="flex items-center gap-1.5 text-slate-700 min-h-[36px]">
            <input type="checkbox" className="w-5 h-5" checked={onlyOpen} onChange={(e) => setOnlyOpen(e.target.checked)} /> 안 끝난 것만
          </label>
        </div>
        {who === 'mine' && myOwners.length === 0 && (
          <div className="text-xs text-amber-700">내 이름으로 맡은 묶음이 없어 전체를 보여 줍니다 — 위에서 담당자를 골라 보세요.</div>
        )}
        {err && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded p-2">{err}</div>}
      </div>

      <ul className="p-2 space-y-2">
        {shown.map((l) => (
          <li key={l.id} className={`rounded border bg-white p-3 ${l.isDone ? 'border-emerald-300 bg-emerald-50/60' : 'border-slate-200'}`}>
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-semibold text-slate-900 break-words">{whereOf(l)}</div>
                <div className="text-sm text-slate-700 break-words">
                  {giftApi.personLabel({ receiver: l.receiver, receiverNote: l.receiverNote, dept: l.receiver ? null : l.dept })}
                  {l.receiver && l.dept && !l.partnerNone && <span className="text-slate-500"> · {l.dept}</span>}
                </div>
                <div className="mt-1 flex items-center gap-1.5 flex-wrap text-xs">
                  <span className="text-slate-700">🎁 {l.itemName ?? <span className="text-amber-700">품목 미정</span>} ×{l.qty}</span>
                  {l.method && <span className={`px-1.5 py-0.5 rounded border ${METHOD_CHIP[l.method]}`}>{l.method}</span>}
                  {effWho !== 'mine' && <span className="text-slate-400">· {l.owner || '담당 없음'}</span>}
                </div>
              </div>
              <button type="button" disabled={doneM.isPending} onClick={() => doneM.mutate({ id: l.id, isDone: !l.isDone })}
                aria-pressed={l.isDone}
                className={`shrink-0 min-w-[64px] min-h-[52px] rounded border-2 text-sm font-bold ${l.isDone ? 'bg-emerald-600 border-emerald-600 text-white active:bg-emerald-700' : 'bg-white border-slate-300 text-slate-700 active:bg-slate-100'}`}>
                {l.isDone ? '✅ 완료' : '완료'}
              </button>
            </div>
            {(l.phone || l.address) && (
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                {l.phone && (
                  <a href={`tel:${l.phone.replace(/[^\d+]/g, '')}`} className="min-h-[40px] px-3 inline-flex items-center rounded border border-slate-300 bg-white text-sm active:bg-slate-100">📞 {l.phone}</a>
                )}
                {l.address && (
                  <a href={`https://map.naver.com/p/search/${encodeURIComponent(l.address)}`} target="_blank" rel="noopener noreferrer"
                    className="min-h-[40px] px-3 inline-flex items-center rounded border border-slate-300 bg-white text-sm active:bg-slate-100">🗺 지도</a>
                )}
              </div>
            )}
            {l.address && <div className="mt-1 text-xs text-slate-500 break-words">{l.address}</div>}
            {l.trackingNo && <div className="mt-1 text-xs text-slate-500 whitespace-pre-wrap">🚚 송장 {l.trackingNo}</div>}
            {l.note && <div className="mt-1 text-xs text-slate-600 whitespace-pre-wrap">📝 {l.note}</div>}
          </li>
        ))}
        {shown.length === 0 && (
          <li className="py-10 text-center text-slate-400 text-sm">{onlyOpen && inScope.length > 0 ? '모두 완료했습니다 👍' : '받는 곳이 없습니다.'}</li>
        )}
      </ul>
    </div>
  );
}
